import { ImportError, ImportErrorCode } from "../workerApi";
import { ShapefileBundleWizardProps, WizardStep } from "../Wizard";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Trans, useTranslation } from "react-i18next";
import FarmIcon from "portal/images/icons/farm.svg?react";
import FieldIcon from "portal/images/icons/field.svg?react";
import React from "react";

interface RenderImportErrorProps {
  error: ImportError;
}

const RenderImportError: React.FC<RenderImportErrorProps> = ({ error }) => {
  const { t } = useTranslation();
  let text: string;

  switch (error.code) {
    case ImportErrorCode.UnrecognizedGeometryType: {
      text = t("views.farms.importFarms.importNotes.unrecognizedGeometryType", {
        type: error.type,
      });
      break;
    }
  }

  return <span className="col-start-2 row-span-1 italic">{text}</span>;
};

export const ChooseDataToImport: WizardStep<ShapefileBundleWizardProps> = {
  stepNameI18NKey: "views.farms.importFarms.step4ChooseDataToImport.stepName",
  Render: ({
    convertedFarms,
    displaySelectedGeoIds,
    toggleSelection,
    focusGeoId,
    farmsForImport,
  }) => {
    const { t } = useTranslation();
    const handleItemSelectionToggle = (
      _event: React.SyntheticEvent | null,
      itemId: string,
      _isSelected: boolean
    ): void => {
      toggleSelection(itemId);
    };

    const handleItemFocus = (event: React.SyntheticEvent | null): void => {
      if (!event?.target) {
        return;
      }
      if (!(event.target instanceof HTMLElement)) {
        console.error(
          "handleItemFocus: target is not an html element?",
          event.target
        );
        return;
      }

      const geoId = event.target.dataset.geoId;

      if (geoId) {
        focusGeoId(geoId);
      }
    };

    return (
      <>
        <div>
          {t("views.farms.importFarms.step4ChooseDataToImport.instructions")}
        </div>
        <SimpleTreeView
          className="border-solid border-gray-300 border-2 flex-auto overflow-y-auto"
          checkboxSelection
          multiSelect
          onItemFocus={handleItemFocus}
          onItemSelectionToggle={handleItemSelectionToggle}
          selectedItems={displaySelectedGeoIds}
        >
          {convertedFarms.map((cvtFarm) => {
            const farmToImport = farmsForImport.find(
              (farm) => farm.id?.id === cvtFarm.farmId
            );

            let label: JSX.Element;

            if (farmToImport && farmToImport.zones.length > 0) {
              label = (
                <Trans
                  i18nKey="views.farms.importFarms.step4ChooseDataToImport.fieldsSelected"
                  values={{
                    farmName: cvtFarm.name,
                    count: farmToImport.zones.length,
                  }}
                />
              );
            } else {
              label = <>{cvtFarm.name}</>;
            }

            return (
              <TreeItem
                key={cvtFarm.farmId}
                itemId={cvtFarm.farmId}
                label={
                  <span>
                    <FarmIcon width={24} height={24} /> {label}
                  </span>
                }
                data-geo-id={cvtFarm.farmId}
              >
                {cvtFarm.fields.map((cvtField) => (
                  <TreeItem
                    key={cvtField.zoneId}
                    itemId={cvtField.zoneId}
                    disabled={!cvtField.canImport}
                    label={
                      <div className="grid grid-cols-[min-content_1fr] gap-2">
                        <FieldIcon
                          width={24}
                          height={24}
                          className="col-start-1 row-span-full"
                        />
                        <span className="col-start-2 row-span-1">
                          {cvtField.fieldName}
                        </span>
                        {cvtField.importError && (
                          <RenderImportError error={cvtField.importError} />
                        )}
                      </div>
                    }
                    data-geo-id={cvtField.zoneId}
                  />
                ))}
              </TreeItem>
            );
          })}
        </SimpleTreeView>
      </>
    );
  },
  mapSelectionEnabled: true,
  canAdvancePastStep: ({ displaySelectedGeoIds: selectedZoneIds }) =>
    selectedZoneIds.length > 0,
};
